import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink, Landmark, UtensilsCrossed, MapPin, Church, Moon, ShoppingBag, Clock, Ticket, Info, Star } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SEO from '@/components/SEO';

import marketEsquilino from '@/assets/market-esquilino.jpg';
import foroRomaDia from '@/assets/foro-roma-dia.jpg';
import foroRomanoPuestaSol from '@/assets/foro-romano-puesta-sol.jpg';
import foroRomanoNoche from '@/assets/foro-romano-noche.jpg';
import marketTrionfaleBuilding from '@/assets/market-trionfale-building.jpg';
import marketRomeStreet from '@/assets/market-rome-street.jpg';
import piazzaSpanishSteps from '@/assets/piazza-spanish-steps.jpg';
import piazzaPantheon from '@/assets/piazza-pantheon.jpg';
import piazzaSantaMaria from '@/assets/piazza-santa-maria.jpg';
import piazzaTrevi from '@/assets/piazza-trevi.jpg';
import piazzaCastelSantangelo from '@/assets/piazza-castel-santangelo.jpg';
import piazzaDelPopolo from '@/assets/piazza-del-popolo.jpg';
import restaurantEvo from '@/assets/restaurant-evohosteria.png';
import restaurantTerra from '@/assets/restaurant-terra-di-siena.jpg';
import restaurantSostegno from '@/assets/restaurant-il-sostegno.jpg';
import restaurantPonte from '@/assets/restaurant-ponte-parione.jpg';
import vaticanApoxyomenos from '@/assets/vatican-apoxyomenos.webp';
import vaticanBaldaquino from '@/assets/vatican-baldaquino.webp';
import vaticanCapillaSixtina from '@/assets/vatican-capilla-sixtina.jpg';
import vaticanEsfera from '@/assets/vatican-esfera.jpg';
import trastevereNight from '@/assets/trastevere-night.webp';

const restaurantRecommendations = [
  { name: 'Evo Hosteria', url: 'https://www.evohosteria.com/', description: 'Modern Italian cuisine with creative flair', image: restaurantEvo },
  { name: 'Ristorante Terra di Siena', url: 'https://www.ristoranteterradisiena.com/', description: 'Tuscan-Roman tradition in an elegant setting', image: restaurantTerra },
  { name: 'Il Sostegno', url: 'https://ilsostegno.it/', description: 'Classic Roman trattoria with authentic recipes', image: restaurantSostegno },
  { name: 'Ponte e Parione', url: 'https://ponteeparione.plateform.app/', description: 'Contemporary dining in the heart of Rome', image: restaurantPonte },
];

type Recommendation = {
  name: string;
  url?: string;
  mapEmbed?: string;
  description: string;
  image?: string;
};

type Tip = {
  icon: typeof Clock;
  title: string;
  text: string;
};

type ExperienceContent = {
  intro: string;
  highlights: string[];
  recommendations: Recommendation[];
  tips: Tip[];
};

const experienceContent: Record<string, ExperienceContent> = {
  colosseum: {
    intro: 'The Colosseum stands as the most enduring symbol of ancient Rome. Built between 70-80 AD, this magnificent amphitheater once held up to 80,000 spectators. Together with the Roman Forum and Palatine Hill, it forms one of the world\'s greatest archaeological areas.',
    highlights: [
      'The Colosseum — the largest amphitheater ever built',
      'Roman Forum — the political and social heart of ancient Rome',
      'Palatine Hill — legendary birthplace of Rome with panoramic views',
      'Arch of Constantine — one of the best-preserved triumphal arches',
      'Via dei Fori Imperiali — the grand boulevard connecting it all',
    ],
    recommendations: [
      { name: 'Colosseum Official Tickets', url: 'https://www.coopculture.it/en/colosseo-e-shop.cfm', description: 'Book official skip-the-line tickets — we strongly recommend booking in advance' },
      { name: 'Underground & Arena Floor Tour', url: 'https://www.coopculture.it/en/colosseo-e-shop.cfm', description: 'Exclusive access to the underground chambers and arena floor' },
      { name: 'Palatine Hill & Roman Forum', url: 'https://www.coopculture.it/en/colosseo-e-shop.cfm', description: 'Included with the Colosseum ticket — don\'t miss these adjacent sites' },
    ],
    tips: [
      { icon: Clock, title: 'Best Time to Visit', text: 'Early morning (8:30 AM opening) or late afternoon for fewer crowds and beautiful light.' },
      { icon: Ticket, title: 'Tickets', text: 'Always book online in advance. The combined ticket covers Colosseum, Forum, and Palatine Hill for 2 days.' },
      { icon: Info, title: 'Our Tip', text: 'Start at the Palatine Hill entrance (less crowded), explore the Forum, then finish at the Colosseum.' },
    ],
  },
  piazzas: {
    intro: 'Rome\'s piazzas are the beating heart of the city — open-air salons where art, history, and daily life converge. Each square tells a story, from Bernini\'s baroque masterpieces to lively markets that have operated for centuries.',
    highlights: [
      'Piazza Navona — Bernini\'s Fountain of the Four Rivers and baroque splendor',
      'Piazza di Spagna — the iconic Spanish Steps and Barcaccia Fountain',
      'Campo de\' Fiori — vibrant morning market and evening aperitivo scene',
      'Piazza del Popolo — monumental gateway to the historic center',
      'Piazza Venezia — the "living room of Rome" with the Altare della Patria',
      'Trevi Fountain — throw a coin and guarantee your return to Rome',
    ],
    recommendations: [
      { name: 'Piazza Navona', mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4284.420682727634!2d12.470493912691627!3d41.89916327111948!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132f6083c19d1c3d%3A0xa35724562e82334a!2sPlaza%20Navona!5e1!3m2!1ses!2spt!4v1771713949491!5m2!1ses!2spt', description: 'Our top pick — visit at sunset when the golden light hits Bernini\'s fountains' },
      { name: 'Spanish Steps', mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4283.962998962533!2d12.480174062691926!3d41.905984571118886!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132f60541678ae75%3A0x7fc0d4978aae690f!2sEscaleras%20de%20la%20Plaza%20de%20Espa%C3%B1a!5e1!3m2!1ses!2spt!4v1771714461792!5m2!1ses!2spt', description: 'Climb to the top for stunning views over Rome\'s rooftops' },
      { name: 'Trevi Fountain', mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4284.295218750297!2d12.480692662691672!3d41.90103327111934!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132f6053278340d5%3A0xf676f1e1cc02bbb6!2sFontana%20di%20Trevi!5e1!3m2!1ses!2spt!4v1771714151466!5m2!1ses!2spt', description: 'Visit before 8 AM or after 10 PM to avoid the biggest crowds' },
      { name: 'Campo de\' Fiori Market', mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4284.661423242027!2d12.469574212691562!3d41.89557492111998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132f6055a903cf7d%3A0x1808fc7ca708f2c4!2sCampo%20de&#39;%20Fiori!5e1!3m2!1ses!2spt!4v1771714287103!5m2!1ses!2spt', description: 'Morning market with fresh produce, spices, and local delicacies' },
    ],
    tips: [
      { icon: Clock, title: 'Best Time', text: 'Early morning for peaceful photos; evening for the magical atmosphere of illuminated fountains.' },
      { icon: Star, title: 'Hidden Gem', text: 'Piazza Mattei with the charming Turtle Fountain — a quiet jewel most tourists miss.' },
      { icon: Info, title: 'Our Tip', text: 'Create a walking route: Piazza del Popolo → Spanish Steps → Trevi → Pantheon → Piazza Navona. About 2 hours.' },
    ],
  },
  vatican: {
    intro: 'Vatican City, the world\'s smallest independent state, houses one of humanity\'s greatest art collections. From Michelangelo\'s breathtaking Sistine Chapel ceiling to Raphael\'s School of Athens, every corner reveals masterpieces that have inspired the world for centuries.',
    highlights: [
      'Sistine Chapel — Michelangelo\'s ceiling and The Last Judgment',
      'St. Peter\'s Basilica — the world\'s largest church and Bernini\'s baldachin',
      'Vatican Museums — 54 galleries with art spanning millennia',
      'Raphael Rooms — stunning frescoes including The School of Athens',
      'St. Peter\'s Square — Bernini\'s magnificent colonnade',
      'Vatican Gardens — serene green spaces with exclusive guided tours',
    ],
    recommendations: [
      { name: 'Vatican Museums Official', url: 'https://www.museivaticani.va/content/museivaticani/en.html', description: 'Book official tickets — skip-the-line access is essential' },
      { name: 'St. Peter\'s Basilica Dome', url: 'https://maps.app.goo.gl/VQCbZ6N5aVKqZfYw7', description: 'Climb the dome for the most spectacular panorama of Rome — free entry to the basilica' },
      { name: 'Castel Sant\'Angelo', url: 'https://maps.app.goo.gl/6Ux8LfHrR5dRQDLY8', description: 'Just a short walk away — the ancient fortress with rooftop views toward St. Peter\'s' },
    ],
    tips: [
      { icon: Clock, title: 'Best Time to Visit', text: 'Wednesday mornings (when the Pope holds audiences in the square) the museums are less crowded.' },
      { icon: Ticket, title: 'Tickets', text: 'Book online at least 2 weeks in advance. Friday evening openings (seasonal) are magical.' },
      { icon: Info, title: 'Our Tip', text: 'Visit the basilica first (free entry, opens at 7 AM), then the museums. Wear clothing that covers shoulders and knees.' },
    ],
  },
  trastevere: {
    intro: 'Trastevere ("across the Tiber") is Rome at its most authentic and charming. Cobblestone streets draped in ivy, ochre-colored buildings, and a vibrant nightlife scene make this the city\'s most beloved bohemian quarter. By day it\'s a peaceful village; by night, it transforms into Rome\'s liveliest neighborhood.',
    highlights: [
      'Santa Maria in Trastevere — one of Rome\'s oldest churches with stunning mosaics',
      'Piazza di Santa Maria — the neighborhood\'s lively heart with outdoor cafés',
      'Via della Lungaretta — charming street lined with artisan shops',
      'Gianicolo Hill — the best sunset viewpoint in all of Rome',
      'Porta Portese Market — Rome\'s largest flea market every Sunday',
      'Testaccio — the adjacent foodie neighborhood with Rome\'s best trattorias',
    ],
    recommendations: [
      { name: 'Isola Tiberina', mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2142.5022173261273!2d12.475507356904735!3d41.89046174278019!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132f604994945227%3A0x38544a35ebc1f2cc!2sIsla%20Tiberina!5e1!3m2!1ses!2spt!4v1771715506450!5m2!1ses!2spt', description: 'The charming Tiber Island, connected by ancient bridges — perfect for a romantic stroll' },
      { name: 'Porta Portese Sunday Market', mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4285.446967667941!2d12.471514912691076!3d41.8838642711211!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132f603a00305ef5%3A0xf226b453fc38c027!2sPorta%20Portese!5e1!3m2!1ses!2spt!4v1771715560243!5m2!1ses!2spt', description: 'Rome\'s biggest flea market, every Sunday morning — vintage finds and local atmosphere' },
      { name: 'Piazza Santa Maria', mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4285.055742212078!2d12.46763901269132!3d41.88969687112063!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132f603879c1a2d7%3A0xfb5ad76ee560a6a2!2sPiazza%20di%20Santa%20Maria%20in%20Trastevere%2C%2000153%20Roma%20RM%2C%20Italia!5e1!3m2!1ses!2spt!4v1771715618048!5m2!1ses!2spt', description: 'The heart of Trastevere — grab an aperitivo and watch the world go by' },
      { name: 'Gianicolo Hill Viewpoint', mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4284.936821693997!2d12.458813912691364!3d41.89146967112039!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132f60401d9c9df9%3A0xcfd56f4c39fc5cf!2zSmFuw61jdWxv!5e1!3m2!1ses!2spt!4v1771715658300!5m2!1ses!2spt', description: 'Walk up for the most breathtaking sunset panorama over Rome — absolutely unmissable' },
    ],
    tips: [
      { icon: Clock, title: 'Best Time', text: 'Daytime for exploring quiet streets and churches; evening (after 8 PM) for the vibrant dining and bar scene.' },
      { icon: Star, title: 'Hidden Gem', text: 'The Basilica of Santa Cecilia has a beautiful crypt and Cavallini\'s medieval frescoes — rarely crowded.' },
      { icon: Info, title: 'Our Tip', text: 'Get lost on purpose. The magic of Trastevere is in wandering its unmarked alleys and discovering unexpected corners.' },
    ],
  },
  markets: {
    intro: 'Rome\'s markets are a feast for the senses — vibrant, noisy, and bursting with color. From the multicultural Esquilino market to Testaccio\'s gourmet food hall, these spaces offer a glimpse into the real daily life of Romans and the incredible diversity of Italian ingredients.',
    highlights: [
      'Mercato Esquilino — Rome\'s largest multicultural market with exotic spices and produce',
      'Mercato Testaccio — the foodies\' paradise in a sleek modern building',
      'Campo de\' Fiori — the most picturesque morning market in the historic center',
      'Mercato Trionfale — the biggest neighborhood market near the Vatican',
      'Eataly Roma — the ultimate Italian food emporium at Ostiense',
    ],
    recommendations: [
      { name: 'Mercato Esquilino', mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4284.653040694637!2d12.504361312691582!3d41.895699871120065!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132f61bd550a3c09%3A0xc5590082466062ad!2sNuovo%20Mercato%20Esquilino!5e1!3m2!1ses!2spt!4v1771714786027!5m2!1ses!2spt', description: 'Incredible variety of international ingredients — near our Suite Termini apartment' },
      { name: 'Mercato Testaccio', mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4285.861433337059!2d12.471265212690875!3d41.87768447112184!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132f6031df9bee07%3A0x8c55bfb65ca41fed!2sMercado%20de%20Testaccio!5e1!3m2!1ses!2spt!4v1771714837571!5m2!1ses!2spt', description: 'Our favorite market — try the supplì at Supplizio and the fresh pasta stalls' },
      { name: 'Campo de\' Fiori', mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4284.661423242028!2d12.469574212691565!3d41.895574921119966!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132f6055a903cf7d%3A0x1808fc7ca708f2c4!2sCampo%20de&#39;%20Fiori!5e1!3m2!1ses!2spt!4v1771714872014!5m2!1ses!2spt', description: 'Historic morning market with local produce, spices, dried pasta, and truffle products' },
      { name: 'Mercato Trionfale', mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4283.72068305219!2d12.450030012692002!3d41.90959567111846!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132f60624c209e13%3A0x5f10c977f6c1d5d7!2sMercato%20Trionfale!5e1!3m2!1ses!2spt!4v1771714909564!5m2!1ses!2spt', description: 'The biggest neighborhood market near the Vatican' },
      { name: 'Eataly Roma Ostiense', mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4286.296817404211!2d12.484032712690661!3d41.871191971122585!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132f61d504c36765%3A0x61207bfe74bea068!2sEataly%20Roma%20Ostiense!5e1!3m2!1ses!2spt!4v1771715017882!5m2!1ses!2spt', description: 'Three floors of Italian gastronomy — restaurants, cooking classes, and premium products' },
    ],
    tips: [
      { icon: Clock, title: 'Best Time', text: 'Visit markets in the morning (8-12). Most close by 2 PM. Campo de\' Fiori is best before 11 AM.' },
      { icon: Star, title: 'Don\'t Miss', text: 'At Testaccio market, try a "trapizzino" — Rome\'s beloved street food invented here by Stefano Callegari.' },
      { icon: Info, title: 'Our Tip', text: 'Ask vendors to let you taste ("posso assaggiare?") — they\'re usually happy to share samples of cheese, olives, and cured meats.' },
    ],
  },
};

const experienceIcons: Record<string, any> = {
  colosseum: Landmark,
  cuisine: UtensilsCrossed,
  piazzas: MapPin,
  vatican: Church,
  trastevere: Moon,
  markets: ShoppingBag,
};

const experienceImages: Record<string, string> = {
  colosseum: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=1200&q=80',
  cuisine: 'https://images.unsplash.com/photo-1498579150354-977475b7ea0b?w=1200&q=80',
  piazzas: 'https://images.unsplash.com/photo-1531572753322-ad063cecc140?w=1200&q=80',
  vatican: vaticanCapillaSixtina,
  trastevere: trastevereNight,
  markets: marketEsquilino,
};

const experienceGallery: Record<string, { src: string; alt: string }[]> = {
  colosseum: [
    { src: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=800&q=80', alt: 'Colosseum exterior' },
    { src: 'https://images.unsplash.com/photo-1555992828-ca4dbe41d294?w=800&q=80', alt: 'Colosseum interior' },
    { src: 'https://images.unsplash.com/photo-1604580864964-0462f5d5b1a8?w=800&q=80', alt: 'Colosseum aerial view' },
    { src: foroRomaDia, alt: 'Foro Romano de día' },
    { src: foroRomanoPuestaSol, alt: 'Foro Romano al atardecer' },
    { src: foroRomanoNoche, alt: 'Foro Romano de noche' },
  ],
  piazzas: [
    { src: piazzaSpanishSteps, alt: 'Escalinata de la Plaza de España' },
    { src: piazzaPantheon, alt: 'Panteón de Roma' },
    { src: piazzaSantaMaria, alt: 'Santa Maria in Trastevere' },
    { src: piazzaTrevi, alt: 'Fontana di Trevi' },
    { src: piazzaCastelSantangelo, alt: 'Castel Sant\'Angelo' },
    { src: piazzaDelPopolo, alt: 'Piazza del Popolo' },
  ],
  vatican: [
    { src: vaticanCapillaSixtina, alt: 'Capilla Sixtina' },
    { src: vaticanBaldaquino, alt: 'Baldaquino de San Pedro' },
    { src: vaticanApoxyomenos, alt: 'Apoxyomenos - Museos Vaticanos' },
    { src: vaticanEsfera, alt: 'Esfera de Pomodoro - Museos Vaticanos' },
  ],
  markets: [
    { src: marketEsquilino, alt: 'Nuovo Mercato Esquilino' },
    { src: marketTrionfaleBuilding, alt: 'Mercato Trionfale - Edificio' },
    { src: marketRomeStreet, alt: 'Mercado callejero en Roma' },
  ],
};

const ExperienceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [mapDialog, setMapDialog] = useState<{ open: boolean; src: string; title: string }>({ open: false, src: '', title: '' });

  if (!id) {
    navigate('/');
    return null;
  }

  const titleKey = id as keyof typeof t.experiences;
  const descKey = `${id}Desc` as keyof typeof t.experiences;
  const title = t.experiences[titleKey] as string;
  const description = t.experiences[descKey] as string;
  const Icon = experienceIcons[id] || Landmark;
  const image = experienceImages[id];
  const content = experienceContent[id];
  const gallery = experienceGallery[id];

  if (!title) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={`${title} — Roma Dream Escape`}
        description={(description || `Discover ${title} in Rome with Roma Dream Escape's curated travel guide.`).slice(0, 158)}
        path={`/experience/${id}`}
        image={image}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: title,
          description,
          image,
          author: { "@type": "Organization", name: "Roma Dream Escape" },
        }}
      />
      <Navigation />
      <main>
        {/* Hero */}
        <div className="relative h-[50vh] min-h-[400px] overflow-hidden">
          <img src={image} alt={title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="container mx-auto">
                <button
                  onClick={() => navigate('/#experiences')}
                  className="mb-6 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary transition-colors hover:text-primary/70"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t.nav.experiences}
                </button>
                <div className="flex items-center gap-4">
                  <Icon className="h-8 w-8 text-primary" />
                  <h1 className="font-serif text-3xl tracking-wide text-foreground md:text-5xl">{title}</h1>
                </div>
                <p className="mt-4 max-w-2xl text-lg text-muted-foreground">{description}</p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-6 py-16">
          {/* Cuisine — restaurants */}
          {id === 'cuisine' && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="mb-8 font-serif text-2xl tracking-wide text-foreground md:text-3xl">
                {t.experiences.recommendedRestaurants}
              </h2>
              <div className="grid gap-6 md:grid-cols-2">
                {restaurantRecommendations.map((restaurant, i) => (
                  <motion.a
                    key={restaurant.name}
                    href={restaurant.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                    className="glass-card group overflow-hidden rounded-lg transition-all duration-300 hover:border-primary/30"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={restaurant.image}
                        alt={restaurant.name}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex items-center justify-between p-6">
                      <div>
                        <h3 className="font-serif text-lg tracking-wide text-foreground group-hover:text-primary transition-colors">
                          {restaurant.name}
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground">{restaurant.description}</p>
                      </div>
                      <ExternalLink className="h-5 w-5 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
                    </div>
                  </motion.a>
                ))}
              </div>
            </motion.div>
          )}

          {/* All other experiences — rich content */}
          {id !== 'cuisine' && content && (
            <div className="space-y-16">
              {/* Introduction */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="max-w-3xl"
              >
                <p className="text-lg leading-relaxed text-muted-foreground">{content.intro}</p>
              </motion.div>

              {/* Photo Gallery */}
              {gallery && gallery.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.25 }}
                >
                  <h2 className="mb-8 font-serif text-2xl tracking-wide text-foreground md:text-3xl">
                    Gallery
                  </h2>
                  <div className="grid gap-4 grid-cols-2 md:grid-cols-3">
                    {gallery.map((photo, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.3 + i * 0.08 }}
                        className="group aspect-[4/3] overflow-hidden rounded-lg"
                      >
                        <img
                          src={photo.src}
                          alt={photo.alt}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                        />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <h2 className="mb-8 font-serif text-2xl tracking-wide text-foreground md:text-3xl">
                  Highlights
                </h2>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {content.highlights.map((highlight, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.35 + i * 0.07 }}
                      className="glass-card flex items-start gap-3 rounded-lg p-5"
                    >
                      <Star className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <p className="text-sm leading-relaxed text-foreground">{highlight}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Recommendations */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <h2 className="mb-8 font-serif text-2xl tracking-wide text-foreground md:text-3xl">
                  Our Recommendations
                </h2>
                <div className="grid gap-6 md:grid-cols-2">
                  {content.recommendations.map((rec, i) => {
                    const isLink = !!rec.url && !rec.mapEmbed;
                    const isMap = !!rec.mapEmbed;
                    const Wrapper = isLink ? 'a' : 'div';
                    const linkProps = isLink
                      ? { href: rec.url, target: '_blank' as const, rel: 'noopener noreferrer' }
                      : {};
                    return (
                      <motion.div
                        key={rec.name}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.45 + i * 0.1 }}
                      >
                        <Wrapper
                          {...linkProps}
                          onClick={isMap ? () => setMapDialog({ open: true, src: rec.mapEmbed!, title: rec.name }) : undefined}
                          className={`glass-card group flex items-center justify-between rounded-lg p-6 transition-all duration-300 hover:border-primary/30 block ${isMap ? 'cursor-pointer' : ''}`}
                        >
                          <div>
                            <h3 className="font-serif text-lg tracking-wide text-foreground group-hover:text-primary transition-colors">
                              {rec.name}
                            </h3>
                            <p className="mt-1 text-sm text-muted-foreground">{rec.description}</p>
                          </div>
                          {(rec.url || rec.mapEmbed) && (
                            <MapPin className="h-5 w-5 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
                          )}
                        </Wrapper>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>

              {/* Tips */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <h2 className="mb-8 font-serif text-2xl tracking-wide text-foreground md:text-3xl">
                  Insider Tips
                </h2>
                <div className="grid gap-6 md:grid-cols-3">
                  {content.tips.map((tip, i) => {
                    const TipIcon = tip.icon;
                    return (
                      <motion.div
                        key={tip.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.55 + i * 0.1 }}
                        className="glass-card rounded-lg p-6"
                      >
                        <div className="mb-3 flex items-center gap-3">
                          <TipIcon className="h-5 w-5 text-primary" />
                          <h3 className="font-serif text-base tracking-wide text-foreground">{tip.title}</h3>
                        </div>
                        <p className="text-sm leading-relaxed text-muted-foreground">{tip.text}</p>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            </div>
          )}

          {/* Fallback for experiences without content */}
          {id !== 'cuisine' && !content && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="prose prose-lg max-w-3xl text-muted-foreground"
            >
              <p className="text-lg leading-relaxed">{description}</p>
            </motion.div>
          )}
        </div>
      </main>
      <Footer />
      <Dialog open={mapDialog.open} onOpenChange={(open) => setMapDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="font-serif text-xl tracking-wide">{mapDialog.title}</DialogTitle>
          </DialogHeader>
          <div className="p-6 pt-2">
            <iframe
              src={mapDialog.src}
              width="100%"
              height="450"
              style={{ border: 0, borderRadius: '0.5rem' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExperienceDetail;
